/* ============================================================================
   agmf-mix.js — a returning shopper's feed, mixed.

   Dorian, 2026-09-23: somebody who comes back should not be dealt the same
   feed in the same order - "if they already saw the old content, it could be
   new content".

   WHAT IT DOES
   On a NEW VISIT (not a new tab, not a reload), inside each kind of content
   the feed deals, the pieces are put in this order:
     1. pieces the library gained since their last visit, newest first,
        dealt one for one with...
     2. ...the pieces they have never been shown: the better half first, in
        the library's own order, with a deeper piece brought in every so
        often - and WHICH deeper piece changes from one visit to the next;
     3. pieces they only glanced at;
     4. pieces they have seen, the ones seen longest ago first.
   Only the order INSIDE one kind changes. The engine still decides which
   kind comes when, and agmf-policy.js still decides what goes to the very
   end of the scroll; nothing here can move a piece across that line.

   WHO COUNTS AS RETURNING
   A visit starts with a load more than 30 minutes after this browser last
   had this product's feed open: 66 of 305 measured returns came back within
   30 minutes, and those were new tabs, not new visits. The count lives in
   localStorage under AGMF_Visit:<handle>, fenced to the visitorID the same
   way agmf-seen.js fences its record. A browser on its first visit - or one
   that cannot keep the count - gets exactly today's order: blend() is handed
   the list it was given.

   HOW IT HOOKS IN
   It wraps AGMFPref.blend, the one call the engine makes on each kind's list
   on the first lap and on every later one. The original blend always runs
   last, so language and colour preference still win. No engine change was
   needed for it; it must load after agmf-pref.js and agmf-policy.js and
   before agilite-media-flow.js (see the section file).

   The product card at the top of the page is NOT mixed: it keeps the order
   saved in Agilite Media, for everyone (Dorian 2026-09-23: build as planned).

   CONSOLE - works on live too; type it and the return value shows
     AGMF_Mix.explain()   what was decided for this page, and why
     AGMF_Mix.check(20)   the first 20 cards: tier, depth, days since shown
     AGMF_Mix.settings()  the switch and the deeper-piece rate in force
     AGMF_Mix.endVisit()  make the next load of this product a new visit
   localStorage.agmf_mix = 'off' turns it off for this browser; 'force' makes
   this browser count as returning (QA).
   ============================================================================ */
(function () {
  'use strict';

  if (window.AGMF_Mix) return;

  var GAP_MS = 1800000;                 // 30 minutes between loads = a new visit
  var KEY = 'AGMF_Visit:';
  var LEDGER = 'AGB_ContentSeen';       // agmf-seen.js's record
  var RUNG = { glanced: 1, looked: 2, opened: 3 };
  var DEFAULTS = { on: true, headRun: 1 };
  var DAY_MS = 86400000;

  function log() {
    try {
      if (window.AGB_consoleCat && window.AGB_consoleCat('interaction')) {
        console.log.apply(console, ['[agmf-mix]'].concat([].slice.call(arguments)));
      }
    } catch (e) {}
  }

  // Copies of agmf-seen.js's, so the two agree on what "today" and "who" mean.
  function today() { return Math.floor(Date.now() / 86400000); }
  function vid() {
    try {
      return localStorage.getItem('visitorID') ||
        (document.cookie.match(/(?:^|;\s*)visitorID=([^;]+)/) || [])[1] || '';
    } catch (e) { return ''; }
  }

  function fnv1a(s) {
    s = String(s);
    var h = 0x811c9dc5;
    for (var i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 16777619) >>> 0;
    }
    return h >>> 0;
  }
  // the engine's seededRandom, so a seed means the same thing on both sides
  function mulberry32(seed) {
    var s = seed >>> 0;
    return function () {
      s = (s + 0x6D2B79F5) >>> 0;
      var r = Math.imul(s ^ (s >>> 15), 1 | s);
      r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
      return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
  }

  function handle() {
    try {
      var el = document.querySelector('.agmf-section[data-product-handle]');
      return (el && el.getAttribute('data-product-handle')) || '';
    } catch (e) { return ''; }
  }

  function isMap(o) {
    return !!o && typeof o === 'object' && Object.prototype.toString.call(o) !== '[object Array]';
  }
  function has(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }

  /* ---- what this browser had been shown BEFORE this page ---------------- */
  // Frozen once, at install. Nothing on this page has been recorded yet when
  // this runs, so every later call - the next lap, a rebuild after a colour
  // pick - answers from the same record and comes out the same.
  function readLedger(who) {
    try {
      var seen = window.AGMF_Seen;
      if (seen && typeof seen.all === 'function') {
        var all = seen.all();
        if (isMap(all)) return all;
      }
    } catch (e) { /* the recorder has not booted: read its record directly */ }
    try {
      var raw = JSON.parse(localStorage.getItem(LEDGER) || 'null');
      if (!raw || raw.v !== 1 || !isMap(raw.k)) return {};
      if (raw.vid && who && raw.vid !== who) return {};   // agmf-seen's own fence
      return raw.k;
    } catch (e) { return {}; }
  }

  /* ---- the visit count --------------------------------------------------- */
  var storageOk = true;

  function loadVisit(who, snap) {
    var rec = null;
    try { rec = JSON.parse(localStorage.getItem(KEY + handle()) || 'null'); }
    catch (e) { storageOk = false; rec = null; }
    if (rec && (rec.v !== 1 || typeof rec.n !== 'number' || !(rec.n >= 0))) rec = null;
    // THE IDENTITY FENCE, the seen record's rule: another visitor's count is
    // not this one's. The seen record wipes itself in the same case.
    if (rec && rec.vid && who && rec.vid !== who) {
      log('identity changed - counting visits afresh');
      rec = null;
    }
    if (!rec) {
      rec = { v: 1, vid: who, n: 0, cur: 0, prev: 0, last: 0 };
      // LAUNCH BOOTSTRAP. Somebody who was here before this file shipped has
      // a seen record and no visit count. The newest day in that record
      // before today was their last visit, so this load is their second -
      // not a first visit that would hand them today's order again.
      var newest = -1, td = today();
      for (var ck in snap) {
        if (!has(snap, ck)) continue;
        var e = snap[ck];
        if (e && typeof e.d === 'number' && e.d < td && e.d > newest) newest = e.d;
      }
      if (newest >= 0) { rec.n = 1; rec.cur = rec.last = newest * DAY_MS; }
    }
    if (!rec.vid && who) rec.vid = who;
    return rec;
  }

  function save(rec) {
    try { localStorage.setItem(KEY + handle(), JSON.stringify(rec)); return true; }
    catch (e) { return false; }
  }

  function bumpVisit(rec, now) {
    if (!rec.last || now - rec.last >= GAP_MS) {
      rec.prev = rec.cur;
      rec.cur = now;
      rec.n += 1;
    }
    rec.last = now;
    if (!save(rec)) storageOk = false;
    // A browser that cannot keep the count cannot be told from a new one,
    // and a new one gets today's order.
    if (!storageOk && rec.n > 1) rec.n = 1;
    return rec;
  }

  // Keeps `last` current while the page is open, so a long read followed by
  // a new tab is still one visit. The stored count is re-read first: another
  // tab may have started a new visit meanwhile, and this page's older count
  // must not overwrite it.
  var ended = false;
  function touch() {
    if (ended) return;
    try {
      var now = Date.now();
      var stored = JSON.parse(localStorage.getItem(KEY + handle()) || 'null');
      var rec = (stored && stored.v === 1 && typeof stored.n === 'number' &&
                 (!stored.vid || !visit.vid || stored.vid === visit.vid)) ? stored : visit;
      rec.last = now;
      save(rec);
    } catch (e) {}
  }

  /* ---- the switches -------------------------------------------------------- */
  // Agilite Media ships them in feed.seenPolicy.returning, so the mix can be
  // switched off or retuned without a theme push; absent = the defaults.
  // headRun is how many of the better half come before each deeper piece.
  function settings() {
    var out = { on: DEFAULTS.on, headRun: DEFAULTS.headRun, override: '' };
    try {
      var sp = window.AGMF_FEED && window.AGMF_FEED.seenPolicy;
      var ret = sp && sp.returning;
      if (isMap(ret)) {
        if (ret.on === false) out.on = false;
        var h = parseInt(ret.headRun, 10);
        if (!isNaN(h)) out.headRun = Math.min(3, Math.max(0, h));
      }
    } catch (e) {}
    try {
      var o = localStorage.getItem('agmf_mix');
      if (o === 'off' || o === 'force') out.override = o;
    } catch (e) {}
    return out;
  }

  // Decided once, on the first call after the feed has arrived - the feed's
  // switch is only known then. Until then the answer is provisional.
  var verdict = null, conf = null;
  function returning() {
    if (verdict) return verdict;
    var s = settings(), v;
    if (s.override === 'off') v = { yes: false, why: 'off-here' };
    else if (!s.on) v = { yes: false, why: 'off-in-feed' };
    else if (s.override === 'force') v = { yes: true, why: 'forced' };
    else if (visit.n < 2) v = { yes: false, why: storageOk ? 'first-visit' : 'no-storage' };
    else if (!ledgerN) v = { yes: false, why: 'nothing-seen' };
    else v = { yes: true, why: 'returning' };
    if (window.AGMF_FEED) { verdict = v; conf = s; }
    return v;
  }

  function needRung() {
    try {
      var sp = window.AGMF_FEED && window.AGMF_FEED.seenPolicy;
      return RUNG[(sp && sp.counts) || 'looked'] || RUNG.looked;
    } catch (e) { return RUNG.looked; }
  }

  // The start of their last visit. Forced with no last visit: a day ago.
  function prevAt() {
    if (visit.prev > 0) return visit.prev;
    return returning().why === 'forced' ? visit.cur - DAY_MS : 0;
  }

  // "2026-09-20 12:00:00" is not ISO to every browser; read it as UTC.
  function addedAt(it) {
    var s = it && it.addedAt;
    if (!s) return NaN;
    var t = Date.parse(s);
    if (isNaN(t)) t = Date.parse(String(s).replace(' ', 'T') + (/(?:[zZ]|[+-]\d\d:?\d\d)$/.test(s) ? '' : 'Z'));
    return t;
  }

  function entry(ck) { return ck && has(snap, ck) && isMap(snap[ck]) ? snap[ck] : null; }

  function tierOf(it, need, prev) {
    var e = entry(it && it.ck);
    if (!e) return prev > 0 && addedAt(it) > prev ? 'new' : 'unseen';
    return (e.x || 0) < need ? 'glanced' : 'seen';
  }

  // THE LIBRARY'S OWN ORDER - the export order, which is the curated one.
  // Sorting each kind back into it first makes every call a function of
  // WHICH pieces are in the list, so a lap comes out the same as a rebuild.
  var rankFeed = null, rankOf = {}, itemOf = {};
  function curatedIndex() {
    var feed = window.AGMF_FEED;
    if (feed && feed !== rankFeed) {
      rankFeed = feed; rankOf = {}; itemOf = {};
      var items = feed.items || [];
      for (var i = 0; i < items.length; i++) {
        var key = items[i] && (items[i].ck || items[i].id);
        if (key && !has(rankOf, key)) { rankOf[key] = i; itemOf[key] = items[i]; }
      }
    }
    return rankOf;
  }

  // Only a kind's list, and only once the feed has arrived. The whole-list
  // language call the engine makes BEFORE the feed is set (feed.items, which
  // the product card also reads) is never touched.
  function mixable(list) {
    if (!window.AGMF_FEED) return false;
    if (!list || list.length < 2) return false;
    var v = !!(list[0] && list[0].video);
    for (var i = 1; i < list.length; i++) {
      if (!!(list[i] && list[i].video) !== v) return false;
    }
    return true;
  }

  function samePieces(a, b) {
    if (!a || !b || a.length !== b.length) return false;
    var used = [];
    for (var i = 0; i < a.length; i++) {
      var found = false;
      for (var j = 0; j < b.length; j++) {
        if (!used[j] && b[j] === a[i]) { used[j] = true; found = true; break; }
      }
      if (!found) return false;
    }
    return true;
  }

  /* ---- the mix: one kind's list, reordered --------------------------------- */
  var stats = {}, reported = false;

  function mix(list) {
    var need = needRung(), prev = prevAt(), rank = curatedIndex();
    var h = conf ? conf.headRun : settings().headRun;
    var dec = [], i;
    for (i = 0; i < list.length; i++) {
      var it = list[i], key = it && (it.ck || it.id);
      dec.push({ it: it, r: key && has(rank, key) ? rank[key] : 1e9, i: i });
    }
    dec.sort(function (a, b) { return (a.r - b.r) || (a.i - b.i); });
    var k0 = dec[0].it || {};
    var kindKey = [k0.contentType || '', k0.platform || '', k0.format || ''].join('|');

    var added = [], unseen = [], glanced = [], seen = [];
    for (i = 0; i < dec.length; i++) {
      var t = tierOf(dec[i].it, need, prev);
      (t === 'new' ? added : t === 'unseen' ? unseen : t === 'glanced' ? glanced : seen).push(dec[i]);
    }
    added.sort(function (a, b) {
      return (addedAt(b.it) - addedAt(a.it)) || (a.r - b.r) || (a.i - b.i);
    });

    // THE BETTER HALF LEADS, A DEEPER PIECE EVERY headRun+1. The first of
    // each kind is always its best unseen piece; which deeper piece follows
    // it is drawn per visit - the same across reloads and tabs of one visit,
    // different on the next one.
    var rest = unseen, head = unseen, deep = [];
    if (h > 0 && unseen.length >= 3) {
      var cut = Math.ceil(unseen.length / 2);
      head = unseen.slice(0, cut);
      deep = unseen.slice(cut);
      var off = Math.floor(mulberry32(visitSeed ^ fnv1a(kindKey))() * deep.length);
      deep = deep.slice(off).concat(deep.slice(0, off));
      rest = [];
      var hi = 0, di = 0;
      while (hi < head.length || di < deep.length) {
        for (var n = 0; n < h && hi < head.length; n++) rest.push(head[hi++]);
        if (di < deep.length) rest.push(deep[di++]);
      }
    }

    // New pieces one for one with the rest, so they never arrive as a block.
    var front = [], a = 0, b = 0;
    while (a < added.length || b < rest.length) {
      if (a < added.length) front.push(added[a++]);
      if (b < rest.length) front.push(rest[b++]);
    }
    glanced.sort(function (a, b) {
      var A = entry(a.it.ck), B = entry(b.it.ck);
      return ((A.n || 0) - (B.n || 0)) || ((A.d || 0) - (B.d || 0)) || (a.r - b.r) || (a.i - b.i);
    });
    // Seen longest ago first: the end of the scroll is where they come back.
    seen.sort(function (a, b) {
      var A = entry(a.it.ck), B = entry(b.it.ck);
      return ((A.d || 0) - (B.d || 0)) || ((A.n || 0) - (B.n || 0)) || (a.r - b.r) || (a.i - b.i);
    });

    var out = [];
    front.concat(glanced, seen).forEach(function (d) { out.push(d.it); });
    // A REORDERING, NEVER A DIFFERENT SET. Anything else and the list goes
    // back exactly as it came.
    if (!samePieces(list, out)) {
      log('mix check failed for', kindKey, '- kept the order as given');
      return list;
    }
    if (!reported) {
      var st = stats[kindKey] || (stats[kindKey] = {
        added: 0, head: 0, deep: 0, glanced: 0, seen: 0, opensWith: (out[0] && out[0].ck) || null
      });
      st.added += added.length; st.head += head.length; st.deep += deep.length;
      st.glanced += glanced.length; st.seen += seen.length;
    }
    return out;
  }

  /* ---- what the shopper got: F12 + one CAE event ---------------------------- */
  function policyLoaded() {
    try { return !!(window.AGMF_Policy && window.AGMF_Policy.explain && window.AGMF_Policy.explain()); }
    catch (e) { return false; }
  }

  function check(n) {
    n = n > 0 ? n : 20;
    var order = [], tail = {};
    try { order = (window.AGMF && window.AGMF._debug && window.AGMF._debug.order()) || []; } catch (e) {}
    try {
      var v = window.AGMF_Policy && window.AGMF_Policy.explain && window.AGMF_Policy.explain();
      if (v) (v.pushed || []).concat(v.restored || []).forEach(function (ck) { tail[ck] = 1; });
    } catch (e) {}
    curatedIndex();
    var need = needRung(), prev = prevAt(), td = today();
    var rows = [], tailStartsAt = -1, lastFreshAt = -1;
    for (var i = 0; i < order.length; i++) {
      var ck = order[i] && order[i].ck;
      if (ck && tail[ck]) { if (tailStartsAt < 0) tailStartsAt = i; }
      else lastFreshAt = i;
      if (i < n) {
        var e = entry(ck);
        rows.push({
          pos: i, ck: ck || null,
          tier: tierOf((ck && has(itemOf, ck)) ? itemOf[ck] : { ck: ck }, need, prev),
          depth: e ? (e.x || 0) : 0,
          daysAgo: e && typeof e.d === 'number' ? td - e.d : null
        });
      }
    }
    return {
      rows: rows, tailStartsAt: tailStartsAt, lastFreshAt: lastFreshAt,
      seenLast: tailStartsAt < 0 || lastFreshAt < tailStartsAt
    };
  }

  function hoursSince() {
    return visit.prev > 0 ? Math.round((visit.cur - visit.prev) / 360000) / 10 : null;
  }

  var reportQueued = false;
  function scheduleReport() {
    if (reportQueued) return;
    reportQueued = true;
    setTimeout(report, 0);
  }

  // Once per page, after the build: one line per kind, a verdict on the first
  // 20 cards, and the agmf_mix event - for EVERY visitor, so the first-time
  // ones are the comparison group.
  function report() {
    try {
      reported = true;
      var v = returning(), c = check(20), s = conf || settings();
      var looked = 0, glancedN = 0, unseenN = 0, addedN = 0;
      c.rows.forEach(function (r) {
        if (r.tier === 'seen') looked++;
        else if (r.tier === 'glanced') glancedN++;
        else { unseenN++; if (r.tier === 'new') addedN++; }
      });
      var tot = { added: 0, head: 0, deep: 0, glanced: 0, seen: 0 };
      for (var k in stats) {
        if (!has(stats, k)) continue;
        var st = stats[k];
        tot.added += st.added; tot.head += st.head; tot.deep += st.deep;
        tot.glanced += st.glanced; tot.seen += st.seen;
        log(k + ': new ' + st.added + ' · head ' + st.head + ' · deep ' + st.deep + ' · glanced ' +
            st.glanced + ' · seen ' + st.seen + ' · opens with ' + st.opensWith);
      }
      if (!v.yes) log('not mixed:', v.why);
      log('first ' + c.rows.length + ' cards: ' + looked + ' already looked · ' + glancedN + ' glanced · ' +
          unseenN + ' unseen · seen-last ' + (c.seenLast ? 'OK' : 'BROKEN at card ' + c.tailStartsAt));
      if (window.CAE_DISABLED === true) return;
      if (window.CAEDev && typeof window.CAEDev.emit === 'function') {
        window.CAEDev.emit('agmf_mix', {
          product_handle: handle() || null,
          returning: v.yes,
          why: v.why,
          visit_n: visit.n,
          hours_since_last: hoursSince(),
          ledger_n: ledgerN,
          head_run: s.headRun,
          policy_loaded: policyLoaded(),
          new_n: tot.added,
          head_n: tot.head,
          deep_n: tot.deep,
          glanced_n: tot.glanced,
          seen_n: tot.seen,
          first20_looked: looked,
          first20_glanced: glancedN,
          first20_new: addedN,
          seen_last_ok: c.seenLast
        });
      }
    } catch (e) {}
  }

  /* ---- install ------------------------------------------------------------ */
  var pref = window.AGMFPref;
  if (!pref || typeof pref.blend !== 'function') {
    log('no preference layer (agmf-pref.js) on this page - nothing to wrap, today\'s order');
    return;
  }

  var who = vid();
  var snap = readLedger(who);
  var ledgerN = 0;
  for (var ck0 in snap) if (has(snap, ck0)) ledgerN++;
  var visit = bumpVisit(loadVisit(who, snap), Date.now());
  var visitSeed = fnv1a(who + '|' + visit.cur);

  try {
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') touch();
    });
    window.addEventListener('pagehide', touch);
  } catch (e) {}

  var orig = pref.blend;
  pref.blend = function (list) {
    var input = list;
    try {
      if (window.AGMF_FEED) scheduleReport();
      if (mixable(list) && returning().yes) input = mix(list);
    } catch (e) { input = list; }
    return orig.call(pref, input);
  };

  window.AGMF_Mix = {
    explain: function () {
      var v = returning();
      return {
        returning: v.yes,
        why: v.why,
        visit: { n: visit.n, cur: visit.cur, prev: visit.prev, last: visit.last },
        prevUsed: prevAt(),
        hoursSinceLast: hoursSince(),
        ledgerN: ledgerN,
        settings: conf || settings(),
        policyLoaded: policyLoaded(),
        kinds: JSON.parse(JSON.stringify(stats))
      };
    },
    check: check,
    settings: settings,
    endVisit: function () {
      ended = true;
      visit.last = 0;
      try {
        var stored = JSON.parse(localStorage.getItem(KEY + handle()) || 'null');
        if (stored && stored.v === 1) { stored.last = 0; save(stored); } else save(visit);
      } catch (e) { save(visit); }
      return 'the next load of this product starts a new visit';
    }
  };

  if (visit.n >= 2) {
    log('visit ' + visit.n + ' · last visit ' + Math.round((visit.cur - visit.prev) / 3600000) + 'h ago · ' +
        ledgerN + ' pieces in this browser\'s history');
  } else {
    log('visit ' + Math.max(1, visit.n) + ' · today\'s order');
  }
})();
